<script setup lang="ts">
import { ACCESS_LEVEL_OPTIONS, ACCESS_PERMISSION_READ } from '@/utils/accessPermission';
import { IconShare } from '@/components/design/icons';
import { computed, ref, watch } from 'vue';
import type { ContractManagementCalendarLinkResult } from '@/api';
import { CalendarService } from '@/services/calendarService';
import { ContractShareService } from '@/services/contractShareService';
import { ContractualPartyShareService } from '@/services/contractualPartyShareService';
import GaiaRecipientSelect from '@/components/design/domain/people/GaiaRecipientSelect.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import { VaultShareService } from '@/services/vaultShareService';
import type { VaultUidGroups } from '@/utils/vaultSelection';
import { useGaiaToast } from '@/composables/useGaiaToast';

interface Props {
  show: boolean;
  contractUids?: string[];
  contractNames?: string[];
  /**
   * What is shared: contracts or contractual parties (`contractUids`, same
   * share model), a calendar (its uid in `contractUids`; calendar viewers
   * have no access level), or a vault selection of files, contracts and
   * folders (`vaultGroups`).
   */
  kind?: 'contract' | 'party' | 'calendar' | 'vault';
  /** The vault selection, for `kind: 'vault'`. */
  vaultGroups?: VaultUidGroups;
}

const props = withDefaults(defineProps<Props>(), {
  contractUids: () => [],
  contractNames: () => [],
  kind: 'contract',
  vaultGroups: () => ({ fileUids: [], contractUids: [], directoryUids: [] }),
});

const noun = computed(() => {
  if (props.kind === 'party') return { one: 'party', many: 'parties' };
  if (props.kind === 'calendar') return { one: 'calendar', many: 'calendars' };
  if (props.kind === 'vault') return { one: 'item', many: 'items' };
  return { one: 'contract', many: 'contracts' };
});

/** How many things are being shared, whatever their kind. */
const itemCount = computed(() =>
  props.kind === 'vault'
    ? props.vaultGroups.fileUids.length +
      props.vaultGroups.contractUids.length +
      props.vaultGroups.directoryUids.length
    : props.contractUids.length
);

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void;
  (e: 'shared'): void;
}>();

const message = useGaiaToast();

/** Who to share with — people and roles, picked in one field. */
const selectedUserUids = ref<string[]>([]);
const selectedRoleUids = ref<string[]>([]);

/** Bitmask applied to every created share (e.g. READ|WRITE = 3). Default Read. */
const selectedPermissionBitmask = ref<number>(ACCESS_PERMISSION_READ);

const sharing = ref(false);

const showModal = computed({
  get: () => props.show,
  set: (value: boolean) => {
    emit('update:show', value);
  },
});

const modalSubtitle = computed(() => {
  const count = itemCount.value;
  return count === 1 && props.contractNames.length === 1
    ? `Share "${props.contractNames[0]}" with people and roles`
    : `Share ${count} ${count === 1 ? noun.value.one : noun.value.many} with people and roles`;
});

const recipientCount = computed(
  () => selectedUserUids.value.length + selectedRoleUids.value.length
);

const canShare = computed(
  () =>
    itemCount.value > 0 &&
    recipientCount.value > 0 &&
    selectedPermissionBitmask.value !== 0 &&
    !sharing.value
);

function runInitialLoad(): void {
  if (!props.show) return;
  selectedUserUids.value = [];
  selectedRoleUids.value = [];
  selectedPermissionBitmask.value = ACCESS_PERMISSION_READ;
}

watch(
  () => props.show,
  visible => {
    if (visible) runInitialLoad();
  },
  { immediate: true }
);

/**
 * Files and folders share through the vault's bulk endpoint, contracts through
 * their own; a mixed selection takes both calls.
 */
async function shareVaultSelection(recipients: {
  userUids: string[];
  roleUids: string[];
  permissions: number;
}): Promise<void> {
  const { fileUids, contractUids, directoryUids } = props.vaultGroups;
  if (fileUids.length > 0 || directoryUids.length > 0) {
    await VaultShareService.bulkShare({ fileUids, directoryUids, ...recipients });
  }
  if (contractUids.length > 0) {
    await ContractShareService.bulkShareContracts({ contractUids, ...recipients });
  }
  message.success(
    itemCount.value === 1 ? 'Shared' : `Shared ${itemCount.value} ${noun.value.many}`
  );
}

/**
 * Sharing a calendar makes the recipients its viewers. The link endpoints
 * report per-uid outcomes; their `error` strings are what get shown. Users and
 * roles go in separate calls, so one can land while the other fails — that is
 * a partial share, not a failed one. Nothing added at all throws.
 */
async function shareCalendar(recipients: {
  userUids: string[];
  roleUids: string[];
}): Promise<void> {
  const [calendarUid] = props.contractUids;
  const outcomes = await Promise.allSettled([
    ...(recipients.userUids.length > 0
      ? [CalendarService.addPermissionUsers(calendarUid, recipients.userUids)]
      : []),
    ...(recipients.roleUids.length > 0
      ? [CalendarService.addPermissionRoles(calendarUid, recipients.roleUids)]
      : []),
  ]);
  const results: ContractManagementCalendarLinkResult[] = outcomes.flatMap(outcome =>
    outcome.status === 'fulfilled' ? [outcome.value] : []
  );
  const errors = [
    ...outcomes.flatMap(outcome =>
      outcome.status === 'rejected'
        ? [outcome.reason instanceof Error ? outcome.reason.message : 'unknown error']
        : []
    ),
    ...results.flatMap(result =>
      result.results.filter(item => !item.success).map(item => item.error ?? 'unknown error')
    ),
  ];
  const added = results.reduce((sum, result) => sum + result.successful, 0);
  if (added === 0) throw new Error(`Failed to share calendar. ${errors.join(' ')}`.trim());
  if (errors.length === 0) {
    message.success('Shared');
    return;
  }
  message.warning(`Shared, but not everyone could be added. ${errors.join(' ')}`);
}

async function handleShare(): Promise<void> {
  if (!canShare.value) return;
  sharing.value = true;
  try {
    const recipients = {
      userUids: selectedUserUids.value,
      roleUids: selectedRoleUids.value,
      permissions: selectedPermissionBitmask.value,
    };
    if (props.kind === 'vault') {
      await shareVaultSelection(recipients);
    } else if (props.kind === 'calendar') {
      await shareCalendar(recipients);
    } else {
      const result =
        props.kind === 'party'
          ? await ContractualPartyShareService.bulkShareParties({
              partyUids: props.contractUids,
              ...recipients,
            })
          : await ContractShareService.bulkShareContracts({
              contractUids: props.contractUids,
              ...recipients,
            });
      message.success(result.message);
    }
    emit('shared');
    emit('update:show', false);
  } catch (error) {
    message.error(error instanceof Error ? error.message : `Failed to share ${noun.value.many}`);
  } finally {
    sharing.value = false;
  }
}
</script>

<template>
  <n-modal
    v-model:show="showModal"
    preset="card"
    class="mass-share-modal"
    style="width: 640px; max-width: 95vw"
    :mask-closable="false"
  >
    <template #header>
      <div class="modal-header">
        <n-icon :size="26" color="#2563eb" class="modal-header-icon">
          <IconShare />
        </n-icon>
        <div>
          <n-text strong style="font-size: 18px">Share {{ noun.many }}</n-text>
          <n-text depth="2" style="font-size: 13px; display: block; margin-top: 2px">
            {{ modalSubtitle }}
          </n-text>
        </div>
      </div>
    </template>

    <!-- Who, and at which level — one line, read as one sentence. -->
    <n-flex align="flex-start" :size="8" :wrap="false">
      <gaia-recipient-select
        v-model:users="selectedUserUids"
        v-model:roles="selectedRoleUids"
        :style="{ flex: 1, minWidth: 0 }"
      />
      <gaia-select
        v-if="props.kind !== 'calendar'"
        v-model:value="selectedPermissionBitmask"
        :options="ACCESS_LEVEL_OPTIONS"
        aria-label="Access level"
        :style="{ width: '132px', flex: 'none' }"
      />
    </n-flex>

    <template #footer>
      <n-flex justify="end" :size="8">
        <n-button :disabled="sharing" @click="showModal = false">Cancel</n-button>
        <n-button type="primary" :loading="sharing" :disabled="!canShare" @click="handleShare">
          <template #icon>
            <n-icon><IconShare /></n-icon>
          </template>
          Share
        </n-button>
      </n-flex>
    </template>
  </n-modal>
</template>

<style scoped>
/* Header chrome Naive's card header can't express: icon beside a two-line title. */
.mass-share-modal :deep(.n-card-header) {
  padding-bottom: 12px;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.modal-header-icon {
  flex-shrink: 0;
  margin-top: 2px;
}
</style>
